/**
 * Client-side Web-AuthN Doctor engine: capability probes + create/get helpers.
 */

import { copyFor } from './platformCopy';

export async function probeEnvironment() {
  const secureContext = window.isSecureContext === true;
  const webauthnApi = typeof window.PublicKeyCredential !== 'undefined';

  let platformAuthenticator = false;
  let conditionalMediation = false;
  let clientCapabilities = null;

  if (webauthnApi) {
    const PKC = window.PublicKeyCredential;
    try {
      platformAuthenticator = await PKC.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (e) {
      platformAuthenticator = false;
    }
    try {
      if (PKC.isConditionalMediationAvailable) {
        conditionalMediation = await PKC.isConditionalMediationAvailable();
      }
    } catch (e) {
      conditionalMediation = false;
    }
    try {
      if (PKC.getClientCapabilities) {
        clientCapabilities = await PKC.getClientCapabilities();
      }
    } catch (e) {
      clientCapabilities = null;
    }
  }

  const ua = navigator.userAgent || '';
  const os = detectOs(ua);
  const browser = detectBrowser(ua);

  return {
    secureContext,
    webauthnApi,
    platformAuthenticator,
    conditionalMediation,
    clientCapabilities,
    os,
    browser,
    userAgent: ua,
  };
}

export function detectOs(ua) {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Android/i.test(ua)) return 'Android';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Unknown';
}

export function detectBrowser(ua) {
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return 'Chrome';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'Safari';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  return 'Unknown';
}

/**
 * Map failures to remediation playbooks (device-local guidance).
 */
export function remediationFor({ env, errorName, failedStep }) {
  const os = env?.os || 'Unknown';
  const items = [];

  if (!env?.secureContext) {
    items.push({
      title: 'Secure context required',
      steps: ['Open this tool over HTTPS (https://doctor.getidee.de).', 'WebAuthn is blocked on plain HTTP.'],
    });
  }

  if (!env?.webauthnApi) {
    items.push({
      title: 'WebAuthn API not available',
      steps: ['Use a modern browser (Chrome, Edge, Safari, or Firefox).', 'Update the browser to the latest version.'],
    });
  }

  if (env?.webauthnApi && !env?.platformAuthenticator) {
    const none = copyFor(os, env?.browser);
    items.push({
      title: none.noPlatformTitle,
      steps: none.noPlatformSteps,
    });
  }

  if (errorName === 'NotAllowedError') {
    items.push({
      title: 'Ceremony cancelled or timed out',
      steps: [
        copyFor(os, env?.browser).prompt,
        'If you cancelled, click Re-test and complete the prompt.',
        'Check enterprise browser policies that may block Passkeys.',
      ],
    });
  }

  if (errorName === 'InvalidStateError') {
    items.push({
      title: 'Credential already exists for this authenticator',
      steps: ['Start a new diagnostic session (Run again).', 'Or remove the previous doctor lab passkey from the browser/OS password manager.'],
    });
  }

  if (errorName === 'NotSupportedError') {
    items.push({
      title: 'Option not supported on this browser/authenticator',
      steps: ['Retry the core platform path.', 'Use Chrome or Edge for broadest support if Safari fails an advanced option.'],
    });
  }

  if (failedStep === 'passkey-store' || (errorName && String(errorName).includes('SYNCED_PASSKEY_REJECTED'))) {
    const storeCopy = copyFor(os, env?.browser);
    items.push({
      title: storeCopy.storeFailTitle,
      steps: storeCopy.storeFailSteps,
    });
  }

  if (errorName === 'SecurityError') {
    items.push({
      title: 'Security / origin error',
      steps: ['Ensure you are on the official doctor host (doctor.getidee.de).', 'Do not embed this page on another domain.'],
    });
  }

  if (items.length === 0 && failedStep) {
    items.push({
      title: `Step failed: ${failedStep}`,
      steps: ['Fix any OS passkey / Hello / Keychain setup issues above.', 'Click Re-test when ready.'],
    });
  }

  return items;
}

export function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlToBuffer(base64url) {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) {
    view[i] = raw.charCodeAt(i);
  }
  return buffer;
}

/** Decode server PublicKeyCredentialCreationOptions for navigator.credentials.create */
export function decodeCreationOptions(publicKey) {
  const copy = { ...publicKey };
  copy.challenge = base64UrlToBuffer(publicKey.challenge);
  if (publicKey.user && publicKey.user.id) {
    copy.user = { ...publicKey.user, id: base64UrlToBuffer(publicKey.user.id) };
  }
  if (publicKey.excludeCredentials) {
    copy.excludeCredentials = publicKey.excludeCredentials.map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id),
    }));
  }
  return copy;
}

/** Decode server PublicKeyCredentialRequestOptions for navigator.credentials.get */
export function decodeRequestOptions(publicKey) {
  const copy = { ...publicKey };
  copy.challenge = base64UrlToBuffer(publicKey.challenge);
  if (publicKey.allowCredentials) {
    copy.allowCredentials = publicKey.allowCredentials.map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id),
    }));
  }
  return copy;
}

/** Encode PublicKeyCredential for Yubico parse*ResponseJson */
export function encodeCredential(credential) {
  const response = credential.response;
  const encoded = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
    response: {},
  };

  if (credential.authenticatorAttachment) {
    encoded.authenticatorAttachment = credential.authenticatorAttachment;
  }

  if (response.attestationObject) {
    encoded.response = {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports: response.getTransports ? response.getTransports() : undefined,
    };
  } else {
    encoded.response = {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
    };
  }

  return encoded;
}

/**
 * API path. App is served at / (https://doctor.getidee.de or localhost).
 * CRA dev proxies /api → localhost:8025/api (see setupProxy.js).
 */
export function apiUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (process.env.NODE_ENV !== 'production') {
    return p;
  }
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}${p}`;
}

export async function apiPost(path, body) {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (res.status === 429) {
    const err = new Error('rate_limited');
    err.status = 429;
    throw err;
  }
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (e) {
    data = { raw: text };
  }
  if (!res.ok) {
    const detail = data.message || data.error || (typeof data.raw === 'string' ? data.raw.replace(/<[^>]+>/g, ' ').trim() : '');
    const err = new Error(detail || `${res.status} ${res.statusText}` || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}
